import React, { useState } from 'react';
import { Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import { StandardContainer } from '../../../../styledComponents/styledComponents';

export default function IpMetricsHelp() {
   const [activeTab, setActiveTab] = useState('general');

   const tab = (id) => setActiveTab(id);

   return (
      <div className='mt-3'>
         <Nav tabs>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'general' })} onClick={() => tab('general')}>
                  General
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'duration' })} onClick={() => tab('duration')}>
                  Duration
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'usage' })} onClick={() => tab('usage')}>
                  Usage
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'geo' })} onClick={() => tab('geo')}>
                  Network & Geo
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink className={classnames({ active: activeTab === 'agent' })} onClick={() => tab('agent')}>
                  Origin & Agent
               </NavLink>
            </NavItem>
            <NavItem>
               <NavLink
                  className={classnames({ active: activeTab === 'concurrency' })}
                  onClick={() => tab('concurrency')}
               >
                  Max Concurrency
               </NavLink>
            </NavItem>
         </Nav>
         <StandardContainer $paddingTopPx={10} $minHeightPx={295} $border='1px solid #c8ced3' $borderTop='none'>
            {activeTab === 'general' && (
               <>
                  <h5>IP</h5>
                  <p>
                     The client IP address from which playback activity originated. All values in the row are aggregated
                     per IP.
                  </p>

                  <h5>First stream start</h5>
                  <p>Timestamp of the first detected playback event for this IP within the selected timeframe.</p>

                  <h5>Last stream end</h5>
                  <p>Timestamp of the last detected playback event for this IP within the selected timeframe.</p>
               </>
            )}

            {activeTab === 'duration' && (
               <>
                  <h5>Duration / Sum</h5>
                  <p>
                     Total accumulated playback duration generated by this IP. Includes all sessions and all streams.
                  </p>

                  <h5>Duration / Avg</h5>
                  <p>
                     Average playback duration per session. Calculated as total duration divided by the number of
                     sessions.
                  </p>
               </>
            )}

            {activeTab === 'usage' && (
               <>
                  <h5>Sessions</h5>
                  <p>
                     Number of unique playback sessions detected for this IP. Each session typically represents one
                     viewer instance.
                  </p>

                  <h5>Streams</h5>
                  <p>
                     Number of distinct streams accessed by this IP. Multiple plays of the same stream are counted once.
                  </p>

                  <h5>Tokens</h5>
                  <p>
                     Number of unique playback tokens used by this IP. High values may indicate shared or automated
                     usage.
                  </p>

                  <h5>Tags</h5>
                  <p>Number of unique tags associated with playback sessions from this IP.</p>
               </>
            )}

            {activeTab === 'geo' && (
               <>
                  <h5>Top Providers</h5>
                  <p>Top 10 providers: Network providers (ISP / ASN) most frequently associated with this IP.</p>

                  <h5>Top Countries</h5>
                  <p>Top 10 countries from which the playback activity was geo-located, based on the client IP.</p>
               </>
            )}
            {activeTab === 'agent' && (
               <>
                  <h5>Top Referrers</h5>
                  <p>
                     Top 10 referrers: The most common referring domains or URLs that led to playback activity from this
                     IP.
                  </p>

                  <h5>Top Browser Agent IDs</h5>
                  <p>
                     Top 10 browser agent IDs: The most frequently detected browser or device user agent identifiers for
                     sessions from this IP.
                  </p>
               </>
            )}

            {activeTab === 'concurrency' && (
               <>
                  <h5>Max Concurrency</h5>
                  <p>Indicates how many streams were used simultaneously by this IP.</p>
                  <p>
                     The selected timeframe is divided into 10-minute intervals. For each interval, the average
                     concurrency is calculated as total viewing duration divided by the effective time span. The
                     displayed value is the peak (highest) average concurrency across all intervals.
                  </p>
                  <details>
                     <summary style={{ cursor: 'pointer', marginBottom: '6px' }}>Show formula</summary>
                     <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '13px' }}>
                        {`timeSpan    = min((streamEnd - streamStart) + avgLogDuration, 600s)
concurrency = durationSum / timeSpan

durationSum      = sum of all reported viewing durations
                   within the 10-minute interval
streamEnd        = timestamp of the last log event
streamStart      = timestamp of the first log event
avgLogDuration   = durationSum / doc_count
                   (accounts for variable log intervals,
                   e.g. short first heartbeat < 60s)
600s             = bucket width cap (10-minute interval)
                   prevents overestimation when sessions
                   span the full interval

displayed value  = max(concurrency) across all intervals`}
                     </pre>
                  </details>
                  <p>
                     Values greater than 1 indicate parallel playback. High values may suggest shared access, proxies,
                     or automated activity.
                  </p>
               </>
            )}
         </StandardContainer>
      </div>
   );
}
